'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { AddNewsSidebar } from '@/components/news/add-news-sidebar'
import { EditNewsSidebar } from './edit-news-sidebar'
import { ReplyNewsSidebar } from './reply-news-sidebar'
import { NewsFilterSidebar, NewsFilterState } from './news-filter-sidebar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/lib/stores/auth-store'
import { getRedmineBaseUrl } from '@/lib/tenant-config'
import {
  Calendar,
  MessageSquare,
  Eye,
  TrendingUp,
  Plus,
  List,
  Grid3X3,
  Edit,
  Trash2,
  Table as TableIcon,
  Filter,
  User as UserIcon,
  Newspaper
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslations, useLocale } from 'next-intl'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

interface NewsArticle {
  id: number
  project: {
    id: number
    name: string
  }
  author: {
    id: number
    name: string
  }
  title: string
  summary: string
  description: string
  created_on: string
  comments_count?: number
}

interface NewsResponse {
  news: NewsArticle[]
  total_count: number
  offset: number
  limit: number
}

interface NewsListProps {
  projectId?: string
  showProjectName?: boolean
}

export function NewsList({ projectId, showProjectName = false }: NewsListProps) {
  const [news, setNews] = useState<NewsArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [limit] = useState(10)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isReplyOpen, setIsReplyOpen] = useState(false)
  const [activeArticle, setActiveArticle] = useState<NewsArticle | null>(null)
  const [layout, setLayout] = useState<'compact' | 'detailed' | 'table'>('table')
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<NewsFilterState>({
    authors: []
  })
  const [articleToDelete, setArticleToDelete] = useState<NewsArticle | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { session } = useAuthStore()
  const router = useRouter()
  const { toast } = useToast()
  const t = useTranslations('newsPage')
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'

  const getAuthHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (session?.credentials?.basicAuth) {
      headers['Authorization'] = `Basic ${session.credentials.basicAuth}`
    } else if (session?.apiKey) {
      headers['X-Redmine-API-Key'] = session.apiKey
    }

    const baseUrl = getRedmineBaseUrl()
    headers['X-Redmine-Base-URL'] = baseUrl

    return headers
  }

  const fetchNews = async (currentOffset: number, append: boolean = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const projectParam = projectId ? `&project_id=${projectId}` : ''
      // Add cache-busting parameter with random value to force refresh
      const cacheBuster = `&_cb=${Date.now()}_${Math.random()}`
      const response = await fetch(`/api/news?offset=${currentOffset}&limit=${limit}${projectParam}${cacheBuster}`, {
        headers: getAuthHeaders(),
        cache: 'no-store',
        // Add these to ensure we don't get cached responses
        next: { revalidate: 0 }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch news')
      }

      const data: NewsResponse = await response.json()

      if (append) {
        setNews(prev => [...prev, ...(data.news || [])])
      } else {
        setNews(data.news || [])
      }

      setTotalCount(data.total_count || 0)
      setOffset(currentOffset)
    } catch (error) {
      if (!append) {
        setNews([])
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchNews(0, false)
  }, [projectId])

  const handleLoadMore = () => {
    const newOffset = offset + limit
    fetchNews(newOffset, true)
  }

  const handleEdit = (article: NewsArticle) => {
    // Close sidebar first if open, then set new article
    if (isEditOpen) {
      setIsEditOpen(false)
      setActiveArticle(null)
      // Use setTimeout to ensure state updates before reopening
      setTimeout(() => {
        setActiveArticle(article)
        setIsEditOpen(true)
      }, 0)
    } else {
      setActiveArticle(article)
      setIsEditOpen(true)
    }
  }

  const handleDelete = (article: NewsArticle) => {
    setArticleToDelete(article)
    setShowDeleteAlert(true)
  }

  const confirmDelete = async () => {
    if (!articleToDelete) return

    setIsDeleting(true)
    try {
      const headers = getAuthHeaders()
      const res = await fetch(`/api/news/${articleToDelete.id}`, { 
        method: 'DELETE', 
        headers,
        cache: 'no-store'
      })
      
      if (!res.ok) {
        throw new Error('Failed to delete')
      }

      // Show success toast
      toast({
        title: 'News Deleted',
        description: 'The news article has been deleted successfully.',
      })

      // Close the dialog
      setShowDeleteAlert(false)
      setArticleToDelete(null)

      // Refresh the list
      await fetchNews(0, false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Delete Failed',
        description: 'Failed to delete the news article. Please try again.',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleReply = (article: NewsArticle) => {
    setActiveArticle(article)
    setIsReplyOpen(true)
  }

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getWordCount = (text: string) => {
    // Remove HTML tags and count words
    const plainText = text.replace(/<[^>]*>/g, ' ')
    return plainText.trim().split(/\s+/).filter(word => word.length > 0).length
  }

  const isShortArticle = (description: string) => {
    return getWordCount(description) < 100
  }

  // Strip markdown and HTML tags for compact excerpts
  const stripMarkdown = (text: string) => {
    if (!text) return ''
    // Remove code blocks and inline code
    return text
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/`[^`]*`/g, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\[(.*?)\]\([^)]*\)/g, '$1')
      .replace(/[#>*_~]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const thisWeekNews = news.filter(article => {
    const articleDate = new Date(article.created_on)
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)
    return articleDate >= weekAgo
  })

  // Apply filters
  const filteredNews = news.filter(article => {
    // Author filter
    if (filters.authors.length > 0 && !filters.authors.includes(article.author?.id)) {
      return false
    }
    return true
  })

  const getActiveFilterCount = () => {
    return filters.authors.length
  }

  return (
    <div className="space-y-6">
      {loading ? (
        <>
          {/* Statistics Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Skeleton className="h-3 w-32 rounded-full" />
                      <Skeleton className="h-10 w-20 rounded-full" />
                    </div>
                    <Skeleton className="h-14 w-14 rounded-lg" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Layout Toggle Skeleton */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1 border rounded-md overflow-hidden">
              <Skeleton className="h-9 w-10 rounded-none" />
              <Skeleton className="h-9 w-10 rounded-none" />
            </div>
            <Skeleton className="h-9 w-16 rounded-md" />
          </div>

          {/* News Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="h-full">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-12 w-12 rounded-md" />
                      <div className="space-y-1">
                        <Skeleton className="h-3 w-24 rounded-full" />
                        <Skeleton className="h-3 w-16 rounded-full" />
                      </div>
                    </div>
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                  <CardContent className="p-4 pt-2 flex-1 flex flex-col">
                    <div className="space-y-2 mb-3">
                      <Skeleton className="h-5 w-full rounded-full" />
                      <Skeleton className="h-4 w-3/4 rounded-full" />
                    </div>
                    <div className="space-y-1 mb-4">
                      <Skeleton className="h-3 w-full rounded-full" />
                      <Skeleton className="h-3 w-full rounded-full" />
                      <Skeleton className="h-3 w-2/3 rounded-full" />
                    </div>
                    <div className="mt-auto pt-3 border-t flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Skeleton className="h-4 w-4 rounded-full" />
                        <Skeleton className="h-3 w-8 rounded-full" />
                      </div>
                      <Skeleton className="h-6 w-6 rounded-full" />
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('totalArticles')}</p>
                    <p className="text-4xl font-bold text-foreground">{totalCount}</p>
                  </div>
                  <div className="rounded-lg p-3 bg-muted/50">
                    <Newspaper className="h-7 w-7 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('thisWeek')}</p>
                    <p className="text-4xl font-bold text-foreground">{thisWeekNews.length}</p>
                  </div>
                  <div className="rounded-lg p-3 bg-muted/50">
                    <TrendingUp className="h-7 w-7 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('latestUpdate')}</p>
                    <p className="text-lg font-bold text-foreground">
                      {news.length > 0 ? getTimeAgo(news[0].created_on) : '-'}
                    </p>
                  </div>
                  <div className="rounded-lg p-3 bg-muted/50">
                    <Calendar className="h-7 w-7 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center space-x-1 border rounded-md overflow-hidden">
                <Button
                  variant={layout === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setLayout('table')}
                  className="px-3 rounded-none h-9"
                  title="Table View"
                >
                  <TableIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant={layout === 'compact' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setLayout('compact')}
                  className="px-3 rounded-none h-9"
                  title="Grid View"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={layout === 'detailed' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setLayout('detailed')}
                  className="px-3 rounded-none h-9"
                  title="Detailed View"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(true)}
                className="relative"
              >
                <Filter className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                {t('filters')}
                {getActiveFilterCount() > 0 && (
                  <Badge variant="secondary" className={cn(isRTL ? 'mr-2' : 'ml-2', 'px-1.5 py-0.5 text-xs')}>
                    {getActiveFilterCount()}
                  </Badge>
                )}
              </Button>
            </div>
            <div>
              <Button onClick={() => setIsSidebarOpen(true)} size="sm" disabled={!projectId} title={!projectId ? t('selectProjectToCreate') : undefined}>
                <Plus className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} /> {t('newPost')}
              </Button>
            </div>
          </div>

          {filteredNews.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-24">
                <div className="rounded-lg bg-muted/50 p-6 mb-6">
                  <Newspaper className="h-16 w-16 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {news.length === 0 ? t('noNewsYetTitle') : t('noMatchingNews')}
                </h3>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  {news.length === 0 ? t('noNewsYetDescription') : t('tryAdjustingFilters')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* News Articles */}
              {layout === 'table' ? (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>{t('title')}</TableHead>
                        <TableHead className="hidden md:table-cell">{t('author')}</TableHead>
                        {showProjectName && <TableHead className="hidden lg:table-cell">{t('project')}</TableHead>}
                        <TableHead className="hidden sm:table-cell">{t('date')}</TableHead>
                        <TableHead className="text-right">{t('actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredNews.map((article, index) => {
                        const isOwner = session?.user?.id === article.author?.id
                        return (
                          <TableRow key={article.id} className="group">
                            <TableCell className="font-medium text-muted-foreground">
                              {index + 1}
                            </TableCell>
                            <TableCell className="max-w-md">
                              <div>
                                <Link
                                  href={`/projects/${article.project.id}/news/${article.id}`}
                                  className="font-medium hover:text-primary transition-colors block mb-1"
                                >
                                  {article.title}
                                </Link>
                                {(article.summary || article.description) && (
                                  <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                                    {stripMarkdown(article.summary || article.description)}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <UserIcon className="h-4 w-4 text-primary" />
                                </div>
                                <span className="text-sm truncate max-w-[150px]">
                                  {article.author?.name || 'Unknown'}
                                </span>
                              </div>
                            </TableCell>
                            {showProjectName && (
                              <TableCell className="hidden lg:table-cell">
                                <Badge variant="secondary" className="truncate max-w-[150px]">
                                  {article.project?.name}
                                </Badge>
                              </TableCell>
                            )}
                            <TableCell className="hidden sm:table-cell text-sm text-muted-foreground whitespace-nowrap">
                              {getTimeAgo(article.created_on)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => router.push(`/projects/${article.project.id}/news/${article.id}`)}
                                  className="h-8 w-8"
                                  title="Read"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleReply(article)}
                                  className="h-8 w-8"
                                  title="Reply"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                                {isOwner && (
                                  <>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => handleEdit(article)}
                                      className="h-8 w-8"
                                      title="Edit"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => handleDelete(article)}
                                      className="h-8 w-8"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </Card>
              ) : layout === 'compact' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {filteredNews.map((article, index) => {
                const date = new Date(article.created_on)
                const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
                const day = date.getDate()
                const year = date.getFullYear()
                const isOwner = session?.user?.id === article.author?.id

                return (
                  <Card key={article.id} className="group h-full">
                    <div className="flex flex-col h-full">
                      <div className="flex items-center justify-between px-3 py-2 gap-2">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex-shrink-0 w-12 text-center">
                            <div className="rounded-md px-2 py-1 bg-muted/30">
                              <div className="text-[10px] font-semibold text-muted-foreground">{month}</div>
                              <div className="text-lg font-bold text-foreground leading-none">{day}</div>
                            </div>
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-foreground truncate">{article.title}</div>
                            <div className="text-[11px] text-muted-foreground mt-1 truncate">
                              {article.author?.name ? `${article.author.name} • ${getTimeAgo(article.created_on)}` : getTimeAgo(article.created_on)}{showProjectName && article.project ? ` • ${article.project.name}` : ''}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* header actions removed for compact layout; actions moved to footer */}
                        </div>
                      </div>

                      <div className="px-3 pb-3 text-sm text-muted-foreground">
                        {(article.summary || article.description) && (
                          <div className="line-clamp-3">
                            <MarkdownPreview content={article.summary || article.description} className="prose-sm max-w-none text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      <div className="mt-auto px-3 pb-3 flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-3 w-3" />
                          <span>{article.comments_count ?? 0}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button size="icon" variant="ghost" onClick={() => router.push(`/projects/${article.project.id}/news/${article.id}`)} title="Read">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <div className="flex items-center gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleReply(article)} title="Reply">
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                            {isOwner && (
                              <>
                                <Button size="icon" variant="ghost" onClick={() => handleEdit(article)} title="Edit">
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => handleDelete(article)} title="Delete">
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>
          ) : (
            <div className="space-y-6">
              {filteredNews.map((article, index) => {
                const date = new Date(article.created_on)
                const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
                const day = date.getDate()
                const year = date.getFullYear()
                const isOwner = session?.user?.id === article.author?.id

                return (
                  <Card key={article.id} className="group">
                    <div className="flex flex-col md:flex-row items-stretch">
                      {/* Left date strip - more visual and compact */}
                      <div className="md:w-36 flex items-center justify-center bg-primary/6 border-r">
                        <div className="text-center p-4">
                          <div className="text-xs font-semibold text-primary uppercase">{month}</div>
                          <div className="text-3xl font-extrabold text-foreground my-1">{day}</div>
                          <div className="text-xs text-muted-foreground">{year}</div>
                        </div>
                      </div>

                      {/* Right content area with prominent summary */}
                      <div className="flex-1 p-6 bg-muted/5">
                        <div className="flex items-start justify-between">
                          <div className="min-w-0">
                            <h2 className="text-2xl md:text-3xl font-extrabold leading-tight truncate">{article.title}</h2>
                            <div className="mt-1 text-sm text-muted-foreground truncate">
                              {article.author.name} • {getTimeAgo(article.created_on)}{showProjectName && article.project ? ` • ${article.project.name}` : ''}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {index === 0 && (
                              <Badge variant="secondary">Featured</Badge>
                            )}
                          </div>
                        </div>

                        {/* Summary block stands out in this layout */}
                        {article.summary && (
                          <p className="mt-4 text-lg text-foreground leading-relaxed">{article.summary}</p>
                        )}

                        {/* Truncated description with inline read more */}
                        {article.description && (
                          <div className="mt-4 text-sm text-muted-foreground">
                            <div className="line-clamp-4">
                              <MarkdownPreview content={article.description} className="max-w-none" />
                            </div>
                            <div className="mt-2">
                              <Link href={`/projects/${article.project.id}/news/${article.id}`} className="text-primary hover:underline font-medium">Read full article →</Link>
                            </div>
                          </div>
                        )}

                        {/* Actions and meta */}
                        <div className="mt-5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleReply(article)} title="Reply">
                              <MessageSquare className="h-4 w-4" />
                              <span className="ml-2 hidden md:inline">Reply</span>
                            </Button>
                            {isOwner && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleEdit(article)} title="Edit">
                                  <Edit className="h-4 w-4" />
                                  <span className="ml-2 hidden md:inline">Edit</span>
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDelete(article)} title="Delete">
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                  <span className="ml-2 hidden md:inline text-red-600">Delete</span>
                                </Button>
                              </>
                            )}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {article.comments_count !== undefined && (
                              <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                <span>{article.comments_count} {article.comments_count === 1 ? 'comment' : 'comments'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>
          )}

          {/* Load More */}
          {totalCount > news.length && (
            <div className="text-center pt-6">
              <Button
                variant="outline"
                size="lg"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading more...' : `Load More Articles (${news.length} of ${totalCount})`}
              </Button>
            </div>
          )}
            </>
          )}
        </>
      )}
      
      {projectId && (
        <AddNewsSidebar
          projectId={projectId}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onSuccess={() => {
            setIsSidebarOpen(false)
            fetchNews(0, false) // Refresh news list
          }}
        />
      )}
      <EditNewsSidebar
        isOpen={isEditOpen}
        article={activeArticle}
        onClose={() => {
          setIsEditOpen(false)
          setActiveArticle(null)
        }}
        onSuccess={async () => {
          // Small delay to ensure Redmine API has processed the update
          await new Promise(resolve => setTimeout(resolve, 300))
          // Refresh the news list with fresh data
          await fetchNews(0, false)
          // Close the sidebar after refresh
          setIsEditOpen(false)
          setActiveArticle(null)
        }}
      />
      <ReplyNewsSidebar
        isOpen={isReplyOpen}
        article={activeArticle}
        onClose={() => setIsReplyOpen(false)}
        onSuccess={() => { setIsReplyOpen(false); setActiveArticle(null); fetchNews(0, false) }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete News Article?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{articleToDelete?.title}"? This action cannot be undone and all comments will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete Article'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filter Sidebar */}
      <NewsFilterSidebar
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
        projectId={projectId}
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  )
}
